import { ApiResponse } from "apisauce";

import { USER_TYPE } from "@constants/Bookings";
import {
  AddExternalGuestSubmitType,
  AddGuestSubmitType,
  BookingSaveApi,
  BookingSlotApi,
  TokenizationStatusResponse,
} from "@models/services/booking";
import { CalculatePriceApi } from "@models/services/pricing";
import { resolveApiUrl, resolveUrl } from "@utils/apiUrlResolver.utils";

import API_URL from "./apiConfig/api.config";
import apiCall from "./apiConfig/apiCall.config";

export async function getBookingSlot({
  bookingDate,
  locationId,
}: BookingSlotApi) {
  return await apiCall.get(
    `${API_URL.BOOKING.BOOKING_SLOT}${locationId}?bookingDate=${bookingDate}`,
    undefined,
    {},
    "v2"
  );
}

export async function getBookingEvents({
  bookingDate,
  isAuthenticated,
  locationId,
}: BookingSlotApi) {
  const apiUrl = resolveApiUrl(API_URL.BOOKING.BOOKING_CALENDAR, {
    isAuthenticated,
    kioskPublicUrl: API_URL.KIOSK.BOOKING_CALENDAR_PUBLIC,
    kioskUrl: API_URL.KIOSK.BOOKING_CALENDAR,
  });

  return await apiCall.get(
    `${apiUrl}${locationId}?bookingDate=${bookingDate}`,
    undefined,
    {},
    "v2"
  );
}

export async function getCalendarParticipant(bookingId: string) {
  return await apiCall.get(
    `${resolveUrl(API_URL.BOOKING.CALENDAR_PARTICIPANT, API_URL.KIOSK.CALENDAR_PARTICIPANT)}${bookingId}/participants`,
    undefined,
    {},
    "v2"
  );
}

export async function saveBookingSlot(payload: BookingSaveApi) {
  return await apiCall.post(
    resolveUrl(API_URL.BOOKING.BOOKING_SAVE, API_URL.KIOSK.BOOKING_SAVE),
    payload,
    {},
    "v2"
  );
}
export async function editBookingSlot(
  payload: BookingSaveApi & { bookingId: string }
) {
  return await apiCall.post(
    `${API_URL.BOOKING.BOOKING_EDIT}${payload.bookingId}`,
    payload,
    {},
    "v2"
  );
}

export async function cancelBooking(bookingId: string) {
  return await apiCall.get(
    `${resolveUrl(API_URL.BOOKING.CANCEL, API_URL.KIOSK.CANCEL_BOOKING)}${bookingId}`
  );
}

export async function clearBooking(bookingId: string, paymentUuid?: string) {
  return await apiCall.post(
    API_URL.BOOKING.CLEAR_BOOKING,
    { bookingId, ...(paymentUuid ? { paymentUuid } : {}) },
    {},
    "v2"
  );
}

export async function calculatePrice(payload: CalculatePriceApi) {
  return await apiCall.post(
    `${resolveUrl(API_URL.BOOKING.CALCULATE_PRICE, API_URL.KIOSK.CALCULATE_PRICE)}`,
    payload,
    {},
    "v2"
  );
}

export async function getQuoteInfo({
  bookingDate,
  excludedBookingUUID,
  locationId,
  selectedTime,
}: {
  locationId: string;
  bookingDate: string;
  selectedTime: string;
  excludedBookingUUID?: string;
}) {
  const API_URL_TO_USE = resolveUrl(
    API_URL.BOOKING.GET_QUOTE_INFO,
    API_URL.KIOSK.GET_QUOTE_INFO
  );

  return await apiCall.post(
    `${API_URL_TO_USE}${locationId}`,
    {
      bookingDate,
      excludedBookingUUID,
      selectedTime,
    },
    {},
    "v2"
  );
}

export async function checkCancelFeasibility(bookingId: string) {
  return await apiCall.get(
    `${resolveUrl(API_URL.BOOKING.CHECK_CANCEL_FEASIBILITY, API_URL.KIOSK.CHECK_CANCEL_FEASIBILITY)}${bookingId}`
  );
}

export async function getPaymentStatus(paymentId: string) {
  return await apiCall.get(`${API_URL.BOOKING.PAYMENT_STATUS}${paymentId}`);
}

export async function getTokenizationStatus(
  referenceId: string | number,
  signal?: AbortSignal
): Promise<ApiResponse<TokenizationStatusResponse>> {
  return await apiCall.get(
    `${API_URL.BOOKING.TOKENIZATION_STATUS}${referenceId}`,
    undefined,
    {},
    "v2",
    signal
  );
}

export async function getGuestList(
  query: string,
  clubId?: string,
  hideLiteProfile?: number
) {
  const API_URL_TO_USE = resolveUrl(
    API_URL.BOOKING.GET_GUEST_LIST,
    API_URL.KIOSK.USER_INFO
  );

  const queryParams: string[] = [];

  if (clubId && clubId !== USER_TYPE.otherContacts) {
    queryParams.push(`club=${clubId}`);
  }

  if (hideLiteProfile !== undefined) {
    queryParams.push(`hideLiteProfile=${hideLiteProfile}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

  return await apiCall.get(
    `${API_URL_TO_USE}${query}${queryString}`,
    undefined,
    {},
    "v2"
  );
}

export async function checkGuestList(payload: AddGuestSubmitType) {
  const API_URL_TO_USE = resolveUrl(
    API_URL.BOOKING.CHECK_GUEST_LIST,
    API_URL.KIOSK.CHECK_GUEST_LIST
  );

  return await apiCall.post(`${API_URL_TO_USE}`, payload, {}, "v2");
}
export async function addGuest(payload: AddGuestSubmitType) {
  const API_URL_TO_USE = resolveUrl(
    API_URL.BOOKING.ADD_GUEST,
    API_URL.KIOSK.ADD_GUEST
  );

  return await apiCall.post(`${API_URL_TO_USE}`, payload, {}, "v2");
}

export async function addExternalGuest(payload: AddExternalGuestSubmitType) {
  return await apiCall.post(
    resolveUrl(
      API_URL.BOOKING.ADD_EXTERNAL_GUEST,
      API_URL.KIOSK.ADD_EXTERNAL_GUEST
    ),
    payload,
    {},
    "v2"
  );
}

export async function getUserFavourites() {
  return await apiCall.get(
    resolveUrl(API_URL.BOOKING.USER_FAVOURITES, API_URL.KIOSK.USER_FAVOURITES),
    undefined,
    {},
    "v2"
  );
}

export async function removeUserFavourites(participantsUuid: string[]) {
  return await apiCall.post(
    resolveUrl(
      API_URL.BOOKING.REMOVE_USER_FAVOURITES,
      API_URL.KIOSK.REMOVE_USER_FAVOURITES
    ),
    {
      participantsUuid,
    },
    {},
    "v2"
  );
}

export async function toggleFavouriteClub(clubId: string, favorite: boolean) {
  return await apiCall.post(
    API_URL.BOOKING.ADD_FAVOURITE_CLUB,
    { clubId, favorite },
    {},
    "v2"
  );
}

const amenitiesCache = new Map<string, Promise<unknown>>();

export async function getAmenities({
  bookingDate,
  bookingId,
  locationId,
  resourceId,
  slotTime,
  startTime,
}: {
  bookingDate: string;
  bookingId?: string;
  locationId: string;
  resourceId?: string;
  slotTime?: number | string;
  startTime: string;
}) {
  const cacheKey = `${locationId}_${bookingDate}_${startTime}_${resourceId || ""}_${slotTime || ""}_${bookingId || ""}`;
  if (amenitiesCache.has(cacheKey)) {
    return amenitiesCache.get(cacheKey);
  }

  const promise = apiCall
    .post(
      resolveUrl(API_URL.BOOKING.GET_AMENITIES, API_URL.KIOSK.GET_AMENITIES),
      {
        bookingDate,
        bookingId,
        locationId,
        resourceId,
        slotTime,
        startTime,
      },
      {},
      "v2"
    )
    .then(
      (response: unknown) => {
        amenitiesCache.delete(cacheKey);
        return response;
      },
      (error: unknown) => {
        amenitiesCache.delete(cacheKey);
        throw error;
      }
    );

  amenitiesCache.set(cacheKey, promise);
  return promise;
}

export async function syncUser(clubId: string) {
  return await apiCall.post(
    `${API_URL.BOOKING.USER_SYNC}${clubId}`,
    undefined,
    {},
    "v2"
  );
}

export async function getUpcomingBookings() {
  return await apiCall.get(
    API_URL.BOOKING.UPCOMING_BOOKINGS,
    undefined,
    {},
    "v2"
  );
}
